(() => {
  if (customElements.get('onyx-nde-steps')) return;
  const STORAGE_KEY = 'onyx.nde.steps.v2';
  const LEGACY_STORAGE_KEY = 'onyx.nde.journey.v1';
  const emptyState = () => ({version:1, uiVersion:2, done:{}, method:'rt', stateCode:'', equipment:'xray'});
  class OnyxNdeSteps extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.initialize(), {once:true, signal:this.abort.signal});
      else this.initialize();
    }
    initialize() {
      this.els = {};
      for (const key of ['launcher','launch','panel','heading','method','canvas','summary','status','back','reset','undo-reset','dialog','dialog-heading','dialog-close','facts','kind','mark','held','unmark','skip','load-status']) this.els[key] = this.querySelector('[data-journey-'+key+']');
      if (!this.els.launch) return;
      this.previewAll = false;
      this.isOpen = false;
      this.addEventListener('click', event => this.click(event), {signal:this.abort.signal});
      this.addEventListener('change', event => this.change(event), {signal:this.abort.signal});
      window.addEventListener('popstate', () => this.historyChanged(), {signal:this.abort.signal});
      window.addEventListener('hashchange', () => this.historyChanged(), {signal:this.abort.signal});
      this.els.dialog.addEventListener('close', () => {
        const previous = this.focusBeforePanel;
        this.focusBeforePanel = null;
        if (previous?.isConnected) previous.focus({preventScroll:true});
      }, {signal:this.abort.signal});
      if (window.location.hash.startsWith('#nde/')) this.open(true);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.els?.dialog.open) this.els.dialog.close();
    }
    el(tag, cls, text) {
      const element = document.createElement(tag);
      if (cls) element.className = cls;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    button(text, action, value = '', cls = 'onj-secondary') {
      const button = this.el('button', cls, text);
      button.type = 'button'; button.dataset.action = action;
      if (value) button.dataset.value = value;
      return button;
    }
    text(key) { return this.data.ui[key] || key; }
    async load() {
      if (this.data) return;
      if (!this.dataPromise) this.dataPromise = (async () => {
        const url = new URL(this.dataset.contentUrl, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid content origin');
        const response = await fetch(url.href, {credentials:'same-origin'});
        if (!response.ok) throw new Error('NDE path unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = doc.querySelector('[data-nde-journey-data]');
        if (!script) throw new Error('NDE content missing');
        const data = JSON.parse(script.textContent);
        if (data.version !== 2 || !data.nodes?.rt_safety || !Array.isArray(data.methods)) throw new Error('Invalid NDE content');
        this.data = data;
        this.state = this.restore();
        this.populateMethods();
      })().catch(error => {this.dataPromise = null; throw error;});
      await this.dataPromise;
    }
    restore() {
      const state = emptyState();
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY) || 'null');
        if (!saved || saved.version !== 1) return state;
        for (const [id, value] of Object.entries(saved.done || {})) {
          const node = this.data.nodes[id];
          if (node && (['completed','held'].includes(value) || (value === 'skipped' && node.optional))) state.done[id] = value;
          else if (!node && /^(?:(?:lt|ir|ae|mfl|gw|gpr|lm|mw|nr|va)_|asnt3_(?:lt|ir|ae|mfl)$)/.test(id) && ['completed','held','skipped'].includes(value)) state.done[id] = value;
        }
        if (saved.uiVersion === 2 && this.data.methods.some(method => method.id === saved.method)) state.method = saved.method;
        if (this.data.states.some(state => state.code === saved.stateCode)) state.stateCode = saved.stateCode;
        if (['xray','ram','both'].includes(saved.equipment)) state.equipment = saved.equipment;
      } catch {}
      return state;
    }
    save() {
      try {localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); return true;}
      catch {this.els.status.textContent = this.text('save_failed'); return false;}
    }
    populateMethods() {
      this.els.method.replaceChildren();
      for (const method of this.data.methods) {
        const option = this.el('option','',method.name); option.value = method.id; this.els.method.append(option);
      }
    }
    async open(fromHistory = false) {
      this.els.launch.disabled = true;
      this.els.launch.setAttribute('aria-busy','true');
      this.els['load-status'].textContent = this.dataset.loadingLabel;
      try {
        await this.load();
        if (fromHistory) this.readHash();
        this.els['load-status'].textContent = '';
        this.els.launcher.hidden = true; this.els.panel.hidden = false;
        this.els.launch.setAttribute('aria-expanded','true');
        this.isOpen = true;
        this.render();
        if (!fromHistory) this.syncHash();
        this.focusPath();
      } catch {
        this.els['load-status'].textContent = this.dataset.errorLabel;
      } finally {
        this.els.launch.disabled = false; this.els.launch.removeAttribute('aria-busy');
      }
    }
    readHash() {
      const id = window.location.hash.slice(5);
      if (this.data.methods.some(method => method.id === id)) this.state.method = id;
    }
    syncHash() {
      const url = new URL(window.location.href); url.hash = '#nde/'+this.state.method;
      if (url.href !== window.location.href) window.history.pushState(null,'',url);
    }
    historyChanged() {
      if (window.location.hash.startsWith('#nde/')) {
        if (!this.data || !this.isOpen) this.open(true);
        else {this.readHash(); this.previewAll = false; this.render();}
      } else if (this.isOpen) this.close(false);
    }
    focusPath() {
      this.els.heading.focus({preventScroll:true});
      this.els.panel.scrollIntoView({block:'start',behavior:'auto'});
    }
    close(history = true) {
      if (this.els.dialog.open) this.els.dialog.close();
      this.isOpen = false; this.els.panel.hidden = true; this.els.launcher.hidden = false;
      this.els.launch.setAttribute('aria-expanded','false');
      if (history && window.location.hash.startsWith('#nde/')) {
        const url = new URL(window.location.href); url.hash = ''; window.history.pushState(null,'',url);
      }
      this.els.launch.focus({preventScroll:true});
    }
    method() {return this.data.methods.find(method => method.id === this.state.method);}
    steps() {return this.method()?.path || [];}
    frontier(steps = this.steps()) {
      let next = 0;
      steps.forEach((id,index) => {if (this.state.done[id]) next = Math.max(next,index+1);});
      return next;
    }
    nodeCard(id, index, next) {
      const node = this.data.nodes[id]; const value = this.state.done[id];
      const complete = value === 'completed' || value === 'held';
      const current = index === next;
      const earlier = index < next && !value;
      const status = value === 'skipped' ? 'skipped' : value === 'held' ? 'already_held' : complete ? 'completed' : earlier ? 'earlier' : current ? 'available' : 'future';
      const card = this.button('','info',id,'onj-node '+(complete ? 'is-complete' : value === 'skipped' ? 'is-skipped' : current ? 'is-current' : earlier ? 'is-earlier' : 'is-future'));
      card.dataset.eligible = String(index <= next);
      const number = this.el('span','onj-node__number',complete ? '' : value === 'skipped' ? '–' : String(index+1));
      number.setAttribute('aria-hidden','true');
      const copy = this.el('span','onj-node__copy');
      copy.append(this.el('span','onj-node__kind',this.text('step')+' '+(index+1)+' · '+this.text('kind_'+node.kind)),this.el('strong','onj-node__name',node.name));
      if (current) copy.append(this.el('span','onj-node__outcome',node.outcome));
      const bottom = this.el('span','onj-node__bottom');
      bottom.append(this.el('span','onj-node__state',this.text(status)),this.el('span','onj-node__requirements',this.text('requirements')));
      copy.append(bottom); card.append(number,copy); return card;
    }
    render() {
      if (!this.data) return;
      this.els.heading.textContent = this.method().name;
      this.els.method.value = this.state.method;
      const steps = this.steps(), next = this.frontier(steps);
      this.els.summary.textContent = next >= steps.length ? this.text('path_complete') : this.text('step')+' '+(next+1)+' '+this.text('of')+' '+steps.length;
      const canvas = this.els.canvas; canvas.replaceChildren();
      const tree = this.el('ol','onj-tree');
      const shown = this.previewAll ? steps : steps.slice(0,Math.min(next+3,steps.length));
      shown.forEach((id,index) => {const row = this.el('li','onj-tree__step'); row.append(this.nodeCard(id,index,next)); tree.append(row);});
      canvas.append(tree);
      if (steps.length > shown.length || this.previewAll) canvas.append(this.button(this.text(this.previewAll ? 'short_path' : 'full_path'),'future','','onj-text-button onj-preview-toggle'));
    }
    select(labelText, key, options, value) {
      const label = this.el('label','onj-select-label',labelText);
      const select = this.el('select','onj-select'); select.dataset.setting = key; select.setAttribute('aria-label',labelText);
      options.forEach(([code,name]) => {const option = this.el('option','',name); option.value = code; select.append(option);});
      select.value = value; label.append(select); return label;
    }
    statePicker(parent) {
      const box = this.el('div','onj-jurisdiction');
      box.append(this.select(this.text('state_label'),'stateCode',[['',this.text('state_placeholder')],...this.data.states.map(state => [state.code,state.name])],this.state.stateCode));
      box.append(this.select(this.text('equipment_label'),'equipment',[['xray',this.text('equipment_xray')],['ram',this.text('equipment_ram')],['both',this.text('equipment_both')]],this.state.equipment));
      if (this.state.stateCode) {
        const selected = this.data.states.find(state => state.code === this.state.stateCode);
        box.append(this.el('strong','onj-state-name',selected.name),this.el('p','onj-small',this.text(this.state.stateCode === 'TX' ? 'texas_note' : 'state_review')));
        const link = this.el('a','',this.state.stateCode === 'TX' ? this.data.sources.texas.name : this.text('state_source'));
        link.href = this.state.stateCode === 'TX' ? this.data.sources.texas.url : this.data.sources.states.url;
        link.target = '_blank'; link.rel = 'noopener noreferrer'; box.append(link);
      }
      parent.append(box);
    }
    showRequirements(id, eligible, trigger) {
      if (!this.data.nodes[id]) return;
      this.activeNode = id; this.activeEligible = eligible; this.focusBeforePanel = trigger;
      this.renderFacts(); this.els.dialog.showModal(); this.els['dialog-close'].focus({preventScroll:true});
    }
    renderFacts() {
      const node = this.data.nodes[this.activeNode]; const done = Boolean(this.state.done[this.activeNode]);
      this.els['dialog-heading'].textContent = node.name;
      this.els.kind.textContent = this.text('kind_'+node.kind);
      const facts = this.els.facts; facts.replaceChildren();
      if (!this.activeEligible && !done) facts.append(this.el('p','onj-small onj-note',this.text('preview_note')));
      if (node.status === 'review') facts.append(this.el('p','onj-review',this.text('verification_review')));
      const dl = this.el('dl','onj-facts');
      for (const [label,copy] of [['panel_outcome',node.outcome],['panel_prereq',node.prereq],['panel_training',node.training+(node.exams ? ' '+node.exams : '')],['panel_experience',node.experience],['panel_provider',node.provider]]) {
        const row = this.el('div'); row.append(this.el('dt','',this.text(label)),this.el('dd','',copy)); dl.append(row);
      }
      facts.append(dl);
      if (node.optional) facts.append(this.el('p','onj-small onj-note',this.text(node.kind === 'radiation' ? 'irrsp_note' : this.activeNode.endsWith('_l2') ? 'level_two_note' : 'level_one_note')));
      if (node.state) this.statePicker(facts);
      const links = this.el('div','onj-official-links');
      for (const id of node.refs || []) {
        const source = this.data.sources[id]; if (!source) continue;
        const link = this.el('a','',source.name+' ↗'); link.href = source.url; link.target = '_blank'; link.rel = 'noopener noreferrer'; links.append(link);
      }
      facts.append(links);
      this.els.mark.hidden = done; this.els.held.hidden = done; this.els.unmark.hidden = !done;
      this.els.skip.hidden = done || !node.optional;
      const blocked = Boolean(node.state && !this.state.stateCode);
      this.els.mark.disabled = blocked || !this.activeEligible;
      this.els.held.disabled = blocked;
      this.els.skip.disabled = !this.activeEligible;
    }
    complete(value) {
      const node = this.data.nodes[this.activeNode]; if (!node) return;
      if (value === 'skipped' && (!node.optional || !this.activeEligible)) return;
      if (value === 'completed' && !this.activeEligible) return;
      if (value && value !== 'skipped' && node.state && !this.state.stateCode) return;
      if (value) this.state.done[this.activeNode] = value; else delete this.state.done[this.activeNode];
      this.els.dialog.close();
      const saved = this.save(); this.render();
      this.els.status.textContent = this.text(!saved ? 'save_failed' : value === 'skipped' ? 'skipped_recorded' : 'recorded');
      (this.els.canvas.querySelector('.is-current') || this.els.heading).focus({preventScroll:true});
    }
    async click(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button');
      if (button?.matches('[data-journey-launch]')) {await this.open(); return;}
      if (!this.data) return;
      if (button?.matches('[data-journey-back]')) {this.close(); return;}
      if (button?.matches('[data-journey-dialog-close]')) {this.els.dialog.close(); return;}
      if (button?.matches('[data-journey-mark]')) {this.complete('completed'); return;}
      if (button?.matches('[data-journey-held]')) {this.complete('held'); return;}
      if (button?.matches('[data-journey-skip]')) {this.complete('skipped'); return;}
      if (button?.matches('[data-journey-unmark]')) {this.complete(''); return;}
      if (button?.matches('[data-journey-reset]')) {
        this.resetBackup = JSON.parse(JSON.stringify(this.state));
        this.steps().forEach(id => delete this.state.done[id]);
        const saved = this.save(); this.previewAll = false; this.render(); this.els['undo-reset'].hidden = false;
        this.els.status.textContent = this.text(saved ? 'reset_done' : 'save_failed'); return;
      }
      if (button?.matches('[data-journey-undo-reset]') && this.resetBackup) {
        this.state = this.resetBackup; this.resetBackup = null; const saved = this.save(); this.render(); this.els['undo-reset'].hidden = true;
        this.els.status.textContent = this.text(saved ? 'saved' : 'save_failed'); return;
      }
      if (event.target === this.els.dialog) {
        const box = this.els.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.els.dialog.close();
      }
      if (button?.dataset.action === 'info') this.showRequirements(button.dataset.value,button.dataset.eligible === 'true',button);
      if (button?.dataset.action === 'future') {
        this.previewAll = !this.previewAll; this.render(); this.els.canvas.querySelector('[data-action="future"]')?.focus({preventScroll:true});
      }
    }
    change(event) {
      const target = event.target; if (!(target instanceof HTMLSelectElement) || !this.data) return;
      if (target === this.els.method) {
        if (!this.data.methods.some(method => method.id === target.value)) return;
        this.state.method = target.value; this.previewAll = false; const saved = this.save(); this.render(); this.syncHash(); this.focusPath();
        this.els.status.textContent = saved ? '' : this.text('save_failed'); return;
      }
      if (!['stateCode','equipment'].includes(target.dataset.setting)) return;
      const setting = target.dataset.setting;
      this.state[setting] = target.value; this.save(); this.renderFacts();
      this.els.dialog.querySelector('[data-setting="'+setting+'"]').focus({preventScroll:true});
    }
  }
  customElements.define('onyx-nde-steps',OnyxNdeSteps);
})();
